import * as yup from 'yup';

import { Box, Grid, Typography, Paper, FormControlLabel, Checkbox } from "@material-ui/core";
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, ChangeEvent } from 'react';
import { StepProps } from './types';
import { Controller, useFormContext } from 'react-hook-form';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      padding: theme.spacing(2)
    },
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    mainTitle: {
      marginBottom: theme.spacing(3),
      fontWeight: 500
    },
    sectionTitle: {
      marginBottom: theme.spacing(2),
      fontWeight: 500
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    checkboxRow: {
      display: 'flex',
      flexWrap: 'wrap',
      //  spaceBetween: 'space-between', 
      gap: theme.spacing(2)
    },
    charCount: {
      textAlign: 'right',
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5)
    }
  })
);

const schema = yup.object().shape({
  evolucaoEfeitos: yup.object().shape({
    riscoVida: yup.boolean(),
    hospitalizacao: yup.boolean(),
    motivouHospitalizacao: yup.boolean(),
    prolongouHospitalizacao: yup.boolean(),
    malformacaoCongenita: yup.boolean(),
    recuperacaoComSequelas: yup.boolean(),
    recuperacaoSemSequelas: yup.boolean(),
    naoRecuperado: yup.boolean(),
    desconhecido: yup.boolean(),
    morte: yup.boolean(),
  }),
  // tratamentoCorretor: yup.string().required('Tratamento corretor é obrigatório'),
  suspendeuMedicamento: yup.string().oneOf(['sim', 'nao', ''], 'Selecione uma opção'),
  diminuiuDose: yup.string().oneOf(['sim', 'nao', ''], 'Selecione uma opção'),
  efeitoDesapareceuSuspensao: yup.string().oneOf(['sim', 'nao', 'N/A', ''], 'Selecione uma opção'),
  efeitoReapareceuReintroducao: yup.string().oneOf(['sim', 'nao', 'N/A', ''], 'Selecione uma opção'),
});

interface FormData {
  evolucaoEfeitos: {
    riscoVida: boolean;
    hospitalizacao: boolean;
    motivouHospitalizacao: boolean;
    prolongouHospitalizacao: boolean;
    malformacaoCongenita: boolean;
    recuperacaoComSequelas: boolean;
    recuperacaoSemSequelas: boolean;
    naoRecuperado: boolean;
    desconhecido: boolean;
    morte: boolean;
  };
  tratamentoCorretor: string;
  dadosRelevantesLaboratorio: string;
  suspendeuMedicamento: 'Sim' | 'Não' | '';
  diminuiuDose: 'Sim' | 'Não' | '';
  efeitoDesapareceuSuspensao: 'Sim' | 'Não' | 'N/A' | '';
  efeitoReapareceuReintroducao: 'Sim' | 'Não' | 'N/A' | '';
}

const getMessages = (formData: any) => {
  const messages: { [key: string]: string } = {};

  if (!formData.medidasTomadas) messages.medidasTomadas = 'Medidas tomadas são obrigatórias';
  if (!formData.resultadoTratamento) messages.resultadoTratamento = 'Resultado do tratamento é obrigatório';
  if (formData.efeitoReapareceuReintroducao === '') messages.efeitoReapareceuReintroducao = 'Informe se o efeito reapareceu';

  return messages;
};

export default function EvolucaoTratamento({ }: StepProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const reduxData = useSelector((state: any) => state.dadosEvolucaoTratamento);
  const [formData, setFormData] = useState<FormData>(reduxData && Object.keys(reduxData).length > 0 ? reduxData : {
    evolucaoEfeitos: {
      riscoVida: false,
      hospitalizacao: false,
      motivouHospitalizacao: false,
      prolongouHospitalizacao: false,
      malformacaoCongenita: false,
      recuperacaoComSequelas: false,
      recuperacaoSemSequelas: false,
      naoRecuperado: false,
      desconhecido: false,
      morte: false
    },
    tratamentoCorretor: '',
    dadosRelevantesLaboratorio: '',
    suspendeuMedicamento: '',
    diminuiuDose: '',
    efeitoDesapareceuSuspensao: '',
    efeitoReapareceuReintroducao: ''
  });
  const { control, formState: { errors }, trigger } = useFormContext();

  // Ouvir solicitação de validação do componente pai (step index 3)
  useEffect(() => {
    const handleValidateCurrentStep = (event: Event) => {
      const custom = event as CustomEvent;
      const stepIndex = custom?.detail?.stepIndex;
      if (stepIndex !== 3) return;
      trigger([
        'tratamentoCorretor',
        'evolucaoEfeitos.__group',
        'suspendeuMedicamento',
        'diminuiuDose',
        'efeitoDesapareceuSuspensao',
        'efeitoReapareceuReintroducao'
      ]).then((isValid) => {
        window.dispatchEvent(new CustomEvent('validationResult', { detail: { isValid } }));
      });
    };
    window.addEventListener('validateCurrentStep', handleValidateCurrentStep);
    return () => window.removeEventListener('validateCurrentStep', handleValidateCurrentStep);
  }, [trigger]);

  const handleCheckboxChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      evolucaoEfeitos: {
        ...prev.evolucaoEfeitos,
        [field]: e.target.checked
      }
    }));
    // Atualiza validação do grupo ao marcar/desmarcar
    trigger('evolucaoEfeitos.__group');
  };

  const handleTratamentoChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      tratamentoCorretor: e.target.value
    }));
  };
  const handleChangeDadosRelevantesLaboratorio = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      dadosRelevantesLaboratorio: e.target.value
    }));
  };

  // console.log(formData)

  const handleExclusiveChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field as keyof FormData] === value ? '' : value
    }));
  };

  useEffect(() => {
    dispatch({
      type: 'dadosEvolucaoTratamento',
      payload: formData
    });
  }, [formData, dispatch]);

  // console.log('Dados Evolução e Tratamento:',formData.evolucaoEfeitos)
  return (
    <Box className={classes.formContainer}>

      {/* Evolução dos Efeitos */}
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Evolução dos efeitos indesejaveis:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            {/* <div className={classes.checkboxGroup}>
              <div className={classes.checkboxRow}> */}
            <FormControlLabel
              control={
                <Checkbox
                  name="riscoVida"
                  id="riscoVida"
                  checked={formData.evolucaoEfeitos.riscoVida}
                  onChange={handleCheckboxChange('riscoVida')}
                  color="primary"
                />
              }
              label="Risco de vida"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="motivouHospitalizacao"
                  id="motivouHospitalizacao"
                  checked={formData.evolucaoEfeitos.motivouHospitalizacao}
                  onChange={handleCheckboxChange('motivouHospitalizacao')}
                  color="primary"
                />
              }
              label="Motivou Hospitalização"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="prolongouHospitalizacao"
                  id="prolongouHospitalizacao"
                  checked={formData.evolucaoEfeitos.prolongouHospitalizacao}
                  onChange={handleCheckboxChange('prolongouHospitalizacao')}
                  color="primary"
                />
              }
              label="Prolongou Hospitalização"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="malformacaoCongenita"
                  id="malformacaoCongenita"
                  checked={formData.evolucaoEfeitos.malformacaoCongenita}
                  onChange={handleCheckboxChange('malformacaoCongenita')}
                  color="primary"
                />
              }
              label="Malformação congênita"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="recuperacaoComSequelas"
                  id="recuperacaoComSequelas"
                  checked={formData.evolucaoEfeitos.recuperacaoComSequelas}
                  onChange={handleCheckboxChange('recuperacaoComSequelas')}
                  color="primary"
                />
              }
              label="Recuperação com sequelas"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="recuperacaoSemSequelas"
                  id="recuperacaoSemSequelas"
                  checked={formData.evolucaoEfeitos.recuperacaoSemSequelas}
                  onChange={handleCheckboxChange('recuperacaoSemSequelas')}
                  color="primary"
                />
              }
              label="Recuperação sem sequelas"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="naoRecuperado"
                  id="naoRecuperado"
                  checked={formData.evolucaoEfeitos.naoRecuperado}
                  onChange={handleCheckboxChange('naoRecuperado')}
                  color="primary"
                />
              }
              label="Não recuperou ainda"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.evolucaoEfeitos.desconhecido}
                  onChange={handleCheckboxChange('desconhecido')}
                  color="primary"
                />
              }
              label="Desconhecido"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="morte"
                  id="morte"
                  checked={formData.evolucaoEfeitos.morte}
                  onChange={handleCheckboxChange('morte')}
                  color="primary"
                />
              }
              label="Morte"
            />
          </Grid>
          {/* </div> */}
        </Grid>

        {/* Controller oculto para validação de grupo (ao menos uma opção) */}
        <Controller
          name="evolucaoEfeitos.__group"
          control={control}
          defaultValue={Object.values(formData.evolucaoEfeitos).some(Boolean)}
          rules={{
            validate: () => (
              Object.values(formData.evolucaoEfeitos).some(Boolean) 
            )
          }}
          render={() => <span style={{ display: 'none' }} />} 
        />
        <FormHelperText error>
          {typeof (errors as any)?.evolucaoEfeitos?.__group?.message === 'string'
            ? (errors as any).evolucaoEfeitos.__group.message
            : ''}
        </FormHelperText>
        {/* </Grid> */}
      </Paper>

      {/* Tratamento Corretor */}
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Tratamento corretor
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="tratamentoCorretor"
              control={control}
              defaultValue={formData.tratamentoCorretor}
              // rules={{ required: 'Tratamento corrector é obrigatório',}}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  multiline
                  rows={4}
                  label="Tratamento corrector"
                  placeholder="Descreva o tratamento corrector..."
                  fullWidth
                  id="tratamentoCorretor"
                  variant="outlined"
                  // error={!!error}
                  // helperText={error ? error.message : ''}
                  onChange={(e) => {
                    field.onChange(e);
                    handleTratamentoChange(e);
                  }}
                  value={field.value}
                />
              )}
            />
            <Typography className={classes.charCount}>
              {formData.tratamentoCorretor.length} caracteres
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Questões sobre Medicamento */}
      <Paper className={classes.paper} elevation={2}>
        {/* <Typography variant="h6" className={classes.sectionTitle}>
          Informações sobre o medicamento
        </Typography> */}

        <Grid container spacing={3}>
          {/* Suspendeu medicamento */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Suspendeu medicamento?
            </Typography>
            <Controller
              name="suspendeuMedicamento"
              control={control}
              defaultValue={formData.suspendeuMedicamento}
              rules={{
                validate: (v) => (v !== '' ? true : 'Selecione uma opção')
              }}
              render={({ field }) => (
                <div className={classes.checkboxRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="suspendeuMedicamento"
                        checked={field.value === 'Sim'}
                        onChange={() => {
                          const newVal = field.value === 'Sim' ? '' : 'Sim';
                          field.onChange(newVal);
                          handleExclusiveChange('suspendeuMedicamento', newVal);
                          trigger('suspendeuMedicamento');
                        }}
                        color="primary"
                      />
                    }
                    label="Sim"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="suspendeuMedicamento"
                        checked={field.value === 'Não'}
                        onChange={() => {
                          const newVal = field.value === 'Não' ? '' : 'Não';
                          field.onChange(newVal);
                          handleExclusiveChange('suspendeuMedicamento', newVal);
                          trigger('suspendeuMedicamento');
                        }}
                        color="primary"
                      />
                    }
                    label="Não"
                  />
                </div>
              )}
            />
            <FormHelperText error>
              {typeof errors?.suspendeuMedicamento?.message === 'string' ? errors?.suspendeuMedicamento?.message : ''}
            </FormHelperText>
          </Grid>

          {/* Diminuiu dose */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Diminuiu dose?
            </Typography>
            <Controller
              name="diminuiuDose"
              control={control}
              defaultValue={formData.diminuiuDose}
              rules={{
                validate: (v) => (v !== '' ? true : 'Selecione uma opção')
              }}
              render={({ field }) => (
                <div className={classes.checkboxRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="diminuiuDose"
                        checked={field.value === 'Sim'}
                        onChange={() => {
                          const newVal = field.value === 'Sim' ? '' : 'Sim';
                          field.onChange(newVal);
                          handleExclusiveChange('diminuiuDose', newVal);
                          trigger('diminuiuDose');
                        }}
                        color="primary"
                      />
                    }
                    label="Sim"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="diminuiuDose"
                        checked={field.value === 'Não'}
                        onChange={() => {
                          const newVal = field.value === 'Não' ? '' : 'Não';
                          field.onChange(newVal);
                          handleExclusiveChange('diminuiuDose', newVal);
                          trigger('diminuiuDose');
                        }}
                        color="primary"
                      />
                    }
                    label="Não"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        id="diminuiuDose"
                        checked={field.value === 'N/A'}
                        onChange={() => {
                          const newVal = field.value === 'N/A' ? 'N/A' : 'N/A';
                          field.onChange(newVal);
                          handleExclusiveChange('diminuiuDose', newVal);
                          trigger('diminuiuDose');
                        }}
                        color="primary"
                      />
                    }
                    label="Não se aplica"
                  />
                </div>
              )}
            />
            <FormHelperText error>
              {typeof errors?.diminuiuDose?.message === 'string' ? errors?.diminuiuDose?.message : ''}
            </FormHelperText>
          </Grid>

          {/* Efeito desapareceu após suspensão */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Efeito desapareceu após suspensão?
            </Typography>
            <Controller
              name="efeitoDesapareceuSuspensao"
              control={control}
              defaultValue={formData.efeitoDesapareceuSuspensao}
              rules={{
                validate: (v) => (v !== '' ? true : 'Selecione uma opção')
              }}
              render={({ field }) => (
                <div className={classes.checkboxRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="efeitoDesapareceuSuspensao"
                        checked={field.value === 'Sim'}
                        onChange={() => {
                          const newVal = field.value === 'Sim' ? '' : 'Sim';
                          field.onChange(newVal);
                          handleExclusiveChange('efeitoDesapareceuSuspensao', newVal);
                          trigger('efeitoDesapareceuSuspensao');
                        }}
                        color="primary"
                      />
                    }
                    label="Sim"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="efeitoDesapareceuSuspensao"
                        checked={field.value === 'Não'}
                        onChange={() => {
                          const newVal = field.value === 'Não' ? '' : 'Não';
                          field.onChange(newVal);
                          handleExclusiveChange('efeitoDesapareceuSuspensao', newVal);
                          trigger('efeitoDesapareceuSuspensao');
                        }}
                        color="primary"
                      />
                    }
                    label="Não"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="efeitoDesapareceuSuspensao"
                        checked={field.value === 'N/A'}
                        onChange={() => {
                          const newVal = field.value === 'N/A' ? 'N/A' : 'N/A';
                          field.onChange(newVal);
                          handleExclusiveChange('efeitoDesapareceuSuspensao', newVal);
                          trigger('efeitoDesapareceuSuspensao');
                        }}
                        color="primary"
                      />
                    }
                    label="Não se aplica"
                  />
                </div>
              )}
            />
            <FormHelperText error>
              {typeof errors?.efeitoDesapareceuSuspensao?.message === 'string' ? errors?.efeitoDesapareceuSuspensao?.message : ''}
            </FormHelperText>
          </Grid>

          {/* Efeito reapareceu após reintrodução */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Efeito reapareceu após reintrodução?
            </Typography>
            <Controller
              name="efeitoReapareceuReintroducao"
              control={control}
              defaultValue={formData.efeitoReapareceuReintroducao}
              rules={{
                validate: (v) => (v !== '' ? true : 'Selecione uma opção')
              }}
              render={({ field }) => (
                <div className={classes.checkboxRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="efeitoReapareceuReintroducao"
                        checked={field.value === 'Sim'}
                        onChange={() => {
                          const newVal = field.value === 'Sim' ? '' : 'Sim';
                          field.onChange(newVal);
                          handleExclusiveChange('efeitoReapareceuReintroducao', newVal);
                          trigger('efeitoReapareceuReintroducao');
                        }}
                        color="primary"
                      />
                    }
                    label="Sim"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="efeitoReapareceuReintroducao"
                        checked={field.value === 'Não'}
                        onChange={() => {
                          const newVal = field.value === 'Não' ? '' : 'Não';
                          field.onChange(newVal);
                          handleExclusiveChange('efeitoReapareceuReintroducao', newVal);
                          trigger('efeitoReapareceuReintroducao');
                        }}
                        color="primary"
                      />
                    }
                    label="Não"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="efeitoReapareceuReintroducao"
                        checked={field.value === 'N/A'}
                        onChange={() => {
                          const newVal = field.value === 'N/A' ? 'N/A' : 'N/A';
                          field.onChange(newVal);
                          handleExclusiveChange('efeitoReapareceuReintroducao', newVal);
                          trigger('efeitoReapareceuReintroducao');
                        }}
                        color="primary"
                      />
                    }
                    label="Não se aplica"
                  />
                </div>
              )}
            />
            <FormHelperText error>
              {typeof errors?.efeitoReapareceuReintroducao?.message === 'string' ? errors?.efeitoReapareceuReintroducao?.message : ''}
            </FormHelperText>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Dados relevantes do laboratório
        </Typography>
        <TextField

          multiline
          rows={4}
          label="Dados relevantes do laboratório"
          // placeholder="Dados relevantes do laboratório..."
          fullWidth
          id="dadosRelevantesLaboratorio"
          variant="outlined"
          // error={!!error}
          // helperText={error ? error.message : ''}
          onChange={handleChangeDadosRelevantesLaboratorio}
          value={formData.dadosRelevantesLaboratorio}
        />
      </Paper>
    </Box >
  );
}